import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import Instruction from './instruction';
import routes from '../../../utils/routes';

import { getTutorials, setActiveTutorial } from '../../../store/tutorials';
import { useAppDispatch, useAppSelector } from '../../../store/configureStore';
import { getCurrentUser } from '../../../services/authService';

import { tutorialConstants } from '../../../utils/constants';
import { User } from '../../../utils/models';

const ProjectDetailsInstruction = () => {
	const [tutorialIndex, setTutorialIndex] = useState(0);
	const [permittedTutorials, setPermittedTutorials] = useState<number[]>([]);

	const { activeTutorial } = useAppSelector(getTutorials);
	const { role } = getCurrentUser() as User;

	const dispatch = useAppDispatch();
	const navigate = useNavigate();

	const renderCurrentInstructionMessage = () => {
		const textStatusMappings = {
			[tutorialConstants.ACTIVATE_PROJECT]: 'Fill the highlighted form to activate the project',
			[tutorialConstants.ACCEPT_REPORT]: "Click the highlighted 'Accept Report' button to accept/reject this report",
			[tutorialConstants.REQUEST_SUPERVISOR_UPDATE]: "Click the 'Request Supervisor Update' button highlighted to reuqest that this project be reassigned!",
			[tutorialConstants.CREATE_REPORT]: 'Click the Create / Update Button highlighted to start generating the report',
			[tutorialConstants.APPROVE_REPORT]: 'Click the Approve Report button to approve this project and send to the customer for acceptance!',
			[tutorialConstants.REVIEW_REPORT]: 'Click the Review Button highlighted  to submit your review of this report',
			[tutorialConstants.VIEW_REPORT]: 'Click the View Report Button highlighted to  to view the generated report!',
			[tutorialConstants.EDIT_PROJECT]: "Click the 'Edit Project' button  to modify the details of this project",
			[tutorialConstants.PROJECT_DETAILS]: `These are the details of your report!`,
			[tutorialConstants.VIEW_PROJECT_PROGRESS]: 'If progress has begun on this report, you can click this button to view the report progress',
		};

		return textStatusMappings[activeTutorial as number];
	};

	const setNextTutorial = (tutorialId: number) => {
		dispatch(setActiveTutorial(tutorialId));
	};

	const handleViewNextTutorial = () => {
		const nextTutorial: number = tutorialIndex + 1;
		if (nextTutorial > permittedTutorials.length - 1) {
			if (role.manageReports) {
				return setNextTutorial(tutorialConstants.NEW_REPORT);
			}

			if (role.reviewReports || role.approveReports) {
				return setNextTutorial(tutorialConstants.VIEW_UNPUBLISHED_REPORT);
			}

			setNextTutorial(tutorialConstants.PROFILE_SETTINGS);
			navigate(routes.profileSettings.fullPath);
		} else {
			setTutorialIndex(nextTutorial);
			setNextTutorial(permittedTutorials[nextTutorial]);
		}
	};

	const initializePermittedTutorials = () => {
		const newPermittedTutorials: number[] = [];
		newPermittedTutorials.push(tutorialConstants.PROJECT_DETAILS);

		if (role.manageProjects) {
			newPermittedTutorials.push(tutorialConstants.EDIT_PROJECT);
		}

		if (role.manageReports) {
			newPermittedTutorials.push(tutorialConstants.CREATE_REPORT);
		}

		if (role.approveReports) {
			newPermittedTutorials.push(tutorialConstants.ACTIVATE_PROJECT);
			newPermittedTutorials.push(tutorialConstants.REQUEST_SUPERVISOR_UPDATE);
			newPermittedTutorials.push(tutorialConstants.APPROVE_REPORT);
		}

		if (role.manageReports || role.approveReports) {
			newPermittedTutorials.push(tutorialConstants.VIEW_PROJECT_PROGRESS);
		}

		if (role.reviewReports) {
			newPermittedTutorials.push(tutorialConstants.REVIEW_REPORT);
		}

		if (role.viewReports) {
			newPermittedTutorials.push(tutorialConstants.ACCEPT_REPORT);
		}

		newPermittedTutorials.push(tutorialConstants.VIEW_REPORT);
		setPermittedTutorials(newPermittedTutorials);
	};

	useEffect(() => {
		initializePermittedTutorials();

		// eslint-disable-next-line
	}, []);

	return (
		<Instruction
			isVisible={permittedTutorials.includes(activeTutorial as number)}
			onProceed={handleViewNextTutorial}
			title="Here are your project details!"
			message={renderCurrentInstructionMessage()}
		/>
	);
};

export default ProjectDetailsInstruction;
